import React, { useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid
} from '@mui/material';
import {
  Download as DownloadIcon
} from '@mui/icons-material';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

/**
 * Kurumsal Karbon Ayak İzi Sertifikası Bileşeni
 * Modern ve profesyonel tasarım
 */
const CarbonCertificate = ({ calculationData, onClose }) => {
  const certificateRef = useRef();
  const { results, formData } = calculationData;

  // Performans kategorisini belirle
  const getPerformanceCategory = (totalEmissions, sector = 'office') => {
    const benchmarks = {
      hotel: { excellent: 30, good: 50, average: 80 },
      manufacturing: { excellent: 100, good: 200, average: 400 },
      office: { excellent: 5, good: 15, average: 30 },
      retail: { excellent: 20, good: 40, average: 70 }
    };
    
    const benchmark = benchmarks[sector] || benchmarks.office;
    
    if (totalEmissions <= benchmark.excellent) {
      return {
        level: 'PLATIN',
        color: '#E5E4E2',
        bgColor: 'linear-gradient(135deg, #E5E4E2 0%, #C0C0C0 100%)',
        score: 95,
        description: 'Mükemmel Performans',
        icon: '🏆'
      };
    } else if (totalEmissions <= benchmark.good) {
      return {
        level: 'ALTIN',
        color: '#FFD700',
        bgColor: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
        score: 85,
        description: 'Çok İyi Performans',
        icon: '🥇'
      };
    } else if (totalEmissions <= benchmark.average) {
      return {
        level: 'GÜMÜŞ',
        color: '#C0C0C0',
        bgColor: 'linear-gradient(135deg, #C0C0C0 0%, #A0A0A0 100%)',
        score: 70,
        description: 'İyi Performans',
        icon: '🥈'
      };
    } else {
      return {
        level: 'BRONZ',
        color: '#CD7F32',
        bgColor: 'linear-gradient(135deg, #CD7F32 0%, #A0522D 100%)',
        score: 60,
        description: 'Gelişim Aşamasında',
        icon: '🥉'
      };
    }
  };

  const performance = getPerformanceCategory(results.total_emissions.ton_co2e, formData.sector);
  const currentDate = new Date().toLocaleDateString('tr-TR');

  // Sertifika indirme fonksiyonu
  const handleDownloadCertificate = async () => {
    try {
      const element = certificateRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: 900,
        height: 650
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('landscape', 'mm', 'a4');
      
      const imgWidth = 297;
      const imgHeight = 210;
      
      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      pdf.save(`${formData.organizationName || 'Organizasyon'}_Karbon_Sertifikasi.pdf`);
    } catch (error) {
      console.error('Sertifika indirme hatası:', error);
    }
  };

  return (
    <Box sx={{ 
      position: 'fixed', 
      top: 0, 
      left: 0, 
      width: '100%', 
      height: '100%', 
      bgcolor: 'rgba(0,0,0,0.9)', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      zIndex: 1300,
      p: 2
    }}>
      <Paper
        ref={certificateRef}
        elevation={24}
        sx={{
          width: 900,
          height: 650,
          p: 0,
          background: '#ffffff',
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 0,
          border: '20px solid #1a237e',
          boxShadow: '0 0 60px rgba(26, 35, 126, 0.4)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `
              linear-gradient(45deg, transparent 48%, rgba(26, 35, 126, 0.03) 49%, rgba(26, 35, 126, 0.03) 51%, transparent 52%),
              linear-gradient(-45deg, transparent 48%, rgba(26, 35, 126, 0.03) 49%, rgba(26, 35, 126, 0.03) 51%, transparent 52%)
            `,
            backgroundSize: '30px 30px'
          }
        }}
      >
        {/* Header Strip */}
        <Box sx={{
          background: 'linear-gradient(135deg, #1a237e 0%, #303f9f 50%, #3f51b5 100%)',
          height: 120,
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          px: 6,
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: -20,
            left: 0,
            right: 0,
            height: 40,
            background: 'linear-gradient(135deg, #1a237e 0%, #303f9f 100%)',
            clipPath: 'polygon(0 0, 100% 0, 95% 100%, 5% 100%)'
          }
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, zIndex: 1 }}>
            <Box sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '3px solid rgba(255,255,255,0.3)',
              backdropFilter: 'blur(10px)'
            }}>
              <Typography variant="h2" sx={{ color: 'white' }}>🌱</Typography>
            </Box>
            <Box>
              <Typography variant="h4" fontWeight="bold" sx={{ color: 'white', mb: 1 }}>
                SÜRDÜRÜLEBİLİRLİK SERTİFİKASI
              </Typography>
              <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                KARBON AYAK İZİ DEĞERLENDİRME RAPORU
              </Typography>
            </Box>
          </Box>
          
          {/* Performance Badge */}
          <Box sx={{ 
            position: 'absolute',
            right: 40,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 1
          }}>
            <Box sx={{
              width: 100,
              height: 100,
              borderRadius: '50%',
              background: performance.bgColor,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              border: `4px solid ${performance.color}`,
              boxShadow: `0 0 30px ${performance.color}40`
            }}>
              <Typography variant="h3" sx={{ mb: -1 }}>
                {performance.icon}
              </Typography>
              <Typography variant="body2" fontWeight="bold" sx={{ color: 'white', fontSize: '0.7rem' }}>
                {performance.level}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Certificate Body */}
        <Box sx={{ p: 6, pt: 4, position: 'relative', zIndex: 1, height: 'calc(100% - 120px)' }}>
          {/* Certification Text */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h6" sx={{ color: '#666', mb: 2, letterSpacing: '0.1em' }}>
              BU SERTİFİKA ŞUNU ONAYLAR Kİ
            </Typography>
            <Typography 
              variant="h3" 
              fontWeight="bold" 
              sx={{ 
                color: '#1a237e',
                mb: 2,
                fontFamily: '"Times New Roman", serif',
                borderBottom: '3px solid #1a237e',
                paddingBottom: 1,
                display: 'inline-block'
              }}
            >
              {formData.organizationName || 'ORGANİZASYON ADI'}
            </Typography>
            <Typography variant="body1" sx={{ color: '#666', maxWidth: 600, mx: 'auto', lineHeight: 1.6 }}>
              Yukarıda adı geçen kuruluş, karbon ayak izi ölçümü ve sürdürülebilirlik 
              performansı değerlendirmesini ISO 14064 standartlarına uygun olarak başarıyla tamamlamış 
              ve {performance.description.toLowerCase()} seviyesinde sonuç elde etmiştir.
            </Typography>
          </Box>

          {/* Metrics Grid */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={3}>
              <Box sx={{ 
                textAlign: 'center',
                p: 2,
                border: '2px solid #e3f2fd',
                borderRadius: 2,
                background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)'
              }}>
                <Typography variant="h4" fontWeight="bold" sx={{ color: '#1a237e' }}>
                  {results.total_emissions.ton_co2e.toFixed(1)}
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', fontWeight: 600 }}>
                  TOPLAM EMİSYON
                </Typography>
                <Typography variant="caption" sx={{ color: '#999' }}>
                  tCO2e
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={3}>
              <Box sx={{ 
                textAlign: 'center',
                p: 2,
                border: `2px solid ${performance.color}30`,
                borderRadius: 2,
                background: `linear-gradient(135deg, ${performance.color}10 0%, ${performance.color}20 100%)`
              }}>
                <Typography variant="h4" fontWeight="bold" sx={{ color: performance.color }}>
                  {performance.score}
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', fontWeight: 600 }}>
                  PERFORMANS PUANI
                </Typography>
                <Typography variant="caption" sx={{ color: '#999' }}>
                  /100
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={3}>
              <Box sx={{ 
                textAlign: 'center',
                p: 2,
                border: '2px solid #e8f5e8',
                borderRadius: 2,
                background: 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c8 100%)'
              }}>
                <Typography variant="h4" fontWeight="bold" sx={{ color: '#2e7d32' }}>
                  {formData.sector?.toUpperCase() || 'GENEL'}
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', fontWeight: 600 }}>
                  SEKTÖR
                </Typography>
                <Typography variant="caption" sx={{ color: '#999' }}>
                  Kategori
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={3}>
              <Box sx={{ 
                textAlign: 'center',
                p: 2,
                border: '2px solid #fff3e0',
                borderRadius: 2,
                background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)'
              }}>
                <Typography variant="h4" fontWeight="bold" sx={{ color: '#f57c00' }}>
                  1 YIL
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', fontWeight: 600 }}>
                  GEÇERLİLİK
                </Typography>
                <Typography variant="caption" sx={{ color: '#999' }}>
                  Süre
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Footer */}
          <Box sx={{ 
            position: 'absolute',
            bottom: 20,
            left: 48,
            right: 48,
            borderTop: '2px solid #1a237e',
            pt: 2
          }}>
            <Grid container alignItems="center">
              <Grid item xs={6}>
                <Typography variant="body2" sx={{ color: '#666', fontWeight: 600 }}>
                  SERTİFİKA TARİHİ: {currentDate}
                </Typography>
                <Typography variant="body2" sx={{ color: '#999' }}>
                  Rapor Dönemi: {formData.periodType || 'Aylık'}
                </Typography>
              </Grid>
              <Grid item xs={6} sx={{ textAlign: 'right' }}>
                <Typography variant="body2" sx={{ color: '#666', fontWeight: 600 }}>
                  ISO 14064 STANDARTLARINA UYGUN
                </Typography>
                <Typography variant="body2" sx={{ color: '#999' }}>
                  Uluslararası Karbon Muhasebesi Standardı
                </Typography>
              </Grid>
            </Grid>
          </Box>

          {/* QR Code Placeholder */}
          <Box sx={{
            position: 'absolute',
            top: 20,
            right: 20,
            width: 60,
            height: 60,
            border: '2px solid #1a237e',
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#ffffff',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <Typography variant="caption" sx={{ color: '#666', textAlign: 'center', fontSize: '0.65rem' }}>
              QR<br/>Code
            </Typography>
          </Box>
        </Box>
      </Paper>
      
      {/* Action Buttons - Overlay dışında */}
      <Box sx={{ 
        position: 'absolute',
        bottom: 30,
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        gap: 3,
        zIndex: 1400
      }}>
        <Button
          variant="contained"
          onClick={handleDownloadCertificate}
          startIcon={<DownloadIcon />}
          sx={{
            bgcolor: '#1a237e',
            color: 'white',
            px: 4,
            py: 1.5,
            borderRadius: 3,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '1rem',
            boxShadow: '0 8px 25px rgba(26, 35, 126, 0.4)',
            '&:hover': {
              bgcolor: '#303f9f',
              transform: 'translateY(-3px)',
              boxShadow: '0 12px 35px rgba(26, 35, 126, 0.6)'
            },
            transition: 'all 0.3s ease'
          }}
        >
          Sertifikayı İndir
        </Button>
        
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{
            borderColor: '#1a237e',
            color: '#1a237e',
            px: 4,
            py: 1.5,
            borderRadius: 3,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '1rem',
            bgcolor: 'rgba(255,255,255,0.9)',
            backdropFilter: 'blur(10px)',
            borderWidth: '2px',
            '&:hover': {
              borderColor: '#303f9f',
              color: '#303f9f',
              bgcolor: 'rgba(255,255,255,1)',
              transform: 'translateY(-3px)',
              boxShadow: '0 8px 25px rgba(26, 35, 126, 0.2)'
            },
            transition: 'all 0.3s ease'
          }}
        >
          Kapat
        </Button>
      </Box>
    </Box>
  );
};

export default CarbonCertificate;
